import React from 'react';

// Statiska klassnamn så att Tailwind JIT plockar upp dem korrekt.
const COLOR_STYLES = {
  blue: {
    wrap: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800',
    label: 'text-blue-600 dark:text-blue-400',
    barTrack: 'bg-blue-100 dark:bg-blue-900',
    barFill: 'bg-blue-500',
  },
  green: {
    wrap: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800',
    label: 'text-green-600 dark:text-green-400',
    barTrack: 'bg-green-100 dark:bg-green-900',
    barFill: 'bg-green-500',
  },
  amber: {
    wrap: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
    label: 'text-amber-600 dark:text-amber-400',
    barTrack: 'bg-amber-100 dark:bg-amber-900',
    barFill: 'bg-amber-500',
  },
};

const CostCard = ({ title, color, amount, subtitle, budget }) => {
  const s = COLOR_STYLES[color] || COLOR_STYLES.blue;
  return (
    <div className={`${s.wrap} border rounded p-4`}>
      <p className={`text-xs font-medium uppercase ${s.label}`}>{title}</p>
      <p className="text-xl font-bold text-foreground mt-1">{amount.toLocaleString('sv-SE')} kr</p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      {budget > 0 && (
        <div className={`mt-1.5 h-1.5 ${s.barTrack} rounded-full overflow-hidden`}>
          <div className={`h-full ${s.barFill} rounded-full`} style={{ width: `${Math.min(100, (amount / budget) * 100)}%` }} />
        </div>
      )}
    </div>
  );
};

const CostReportCard = ({ costSummary }) => {
  if (!costSummary) return null;
  return (
    <div className="bg-card border border-border rounded p-6" data-testid="cost-report">
      <h3 className="text-base font-bold mb-4">Sammanställd kostnadsrapport</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <CostCard title="Material" color="blue" amount={costSummary.material_cost} budget={costSummary.budget_material} />
        <CostCard
          title="Arbete"
          color="green"
          amount={costSummary.labor_cost}
          subtitle={`${costSummary.labor_hours} tim × ${costSummary.labor_rate} kr`}
          budget={costSummary.budget_labor}
        />
        <CostCard
          title="Maskiner"
          color="amber"
          amount={costSummary.machine_cost}
          subtitle={`${costSummary.machine_hours} tim × ${costSummary.machine_rate} kr`}
          budget={costSummary.budget_machines}
        />
      </div>
      {costSummary.eta_cost > 0 && (
        <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded p-3 mb-4">
          <span className="text-xs font-medium text-purple-600 dark:text-purple-400 uppercase">ÄTA-arbete: </span>
          <span className="text-sm font-bold text-foreground">{costSummary.eta_cost.toLocaleString('sv-SE')} kr</span>
        </div>
      )}
      <div className="border-t-2 border-border pt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <p className="text-sm text-muted-foreground">Total kostnad</p>
          <p className="text-2xl font-bold text-foreground">{costSummary.total_cost.toLocaleString('sv-SE')} kr</p>
        </div>
        {costSummary.rot_eligible && costSummary.rot_deduction > 0 && (
          <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-700 rounded px-4 py-2 text-right" data-testid="rot-summary">
            <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">ROT-avdrag (30% av arbete)</p>
            <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">-{costSummary.rot_deduction.toLocaleString('sv-SE')} kr</p>
            <p className="text-lg font-bold text-foreground mt-0.5">Efter ROT: {costSummary.total_after_rot.toLocaleString('sv-SE')} kr</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostReportCard;
